/**
 * Debenture (MR01) reconciliation — Emma, 30 Oct 2026: "double check there are no debentures outstanding to be filed".
 * Every UK deal funded in the window that carries a debenture: is it diarised on the Legal Calendar, is it in the
 * audit trail, and is a charge actually registered at Companies House?
 */
import { useEffect, useState } from "react";
import { Loader2, ExternalLink, ShieldCheck, AlertTriangle, CalendarPlus, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getDebentureReconciliation, diariseMissingDebentures } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS = {
  registered: { label: "Registered at Companies House", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  diarised: { label: "Diarised — not yet filed", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  marked_filed: { label: "Marked filed (CH not checked)", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  marked_filed_not_at_ch: { label: "Marked filed — NOT at Companies House", cls: "bg-red-50 text-red-700 border-red-200" },
  missing: { label: "MISSING — not diarised, not at CH", cls: "bg-red-50 text-red-700 border-red-200" },
};
const SOURCE = { contract_pack: "contract pack", docusign: "DocuSign", uw_note: "UW note", manual: "ticked by hand", application: "application" };
const d = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "—");

export default function DebentureReconciliation({ open, onClose, onDiarised }) {
  const { user } = useAuth();
  const [months, setMonths] = useState("6");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await getDebentureReconciliation(parseInt(months, 10), "UK")); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open, months]); // eslint-disable-line react-hooks/exhaustive-deps

  const missing = (data?.rows || []).filter((r) => r.status === "missing");
  const diarise = async () => {
    setBusy(true);
    try {
      const r = await diariseMissingDebentures(parseInt(months, 10), "UK", user?.email || user?.name || "reconciliation");
      toast.success(`${r.count} filing${r.count === 1 ? "" : "s"} diarised on the Legal Calendar`);
      await load();
      onDiarised?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const c = data?.counts || {};
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl" data-testid="debenture-reconciliation">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Debenture filing check (MR01)</DialogTitle>
          <DialogDescription className="text-xs">
            Every UK deal funded in the window (Monday Funded list + portal) that carries a debenture, checked against the Legal Calendar, the audit trail and the live Companies House charges register.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="deb-months"><SelectValue /></SelectTrigger>
            <SelectContent>{["3", "6", "12", "24"].map((m) => <SelectItem key={m} value={m}>Last {m} months</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} disabled={loading} data-testid="deb-refresh"><RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Re-check</Button>
          {data && (
            <div className="flex flex-wrap gap-1.5 text-[11px] ml-auto" data-testid="deb-counts">
              <span className="rounded-full border px-2 py-0.5">{data.deals} funded</span>
              <span className="rounded-full border px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">{c.registered || 0} registered at CH</span>
              <span className="rounded-full border px-2 py-0.5 bg-amber-50 text-amber-800 border-amber-200">{c.diarised || 0} diarised</span>
              <span className="rounded-full border px-2 py-0.5 bg-red-50 text-red-700 border-red-200" title={`${c.missing || 0} not diarised and not at Companies House${c.marked_filed_not_at_ch ? ` · ${c.marked_filed_not_at_ch} marked filed but no charge at Companies House` : ""}`}>{(c.missing || 0) + (c.marked_filed_not_at_ch || 0)} need attention</span>
              <span className="rounded-full border px-2 py-0.5 text-muted-foreground">{c.no_debenture || 0} no debenture</span>
            </div>
          )}
          {missing.length > 0 && (
            <Button size="sm" className="h-8 text-xs bg-red-700 hover:bg-red-800" onClick={diarise} disabled={busy} data-testid="deb-diarise-missing">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CalendarPlus className="w-3 h-3 mr-1" />} Diarise {missing.length} missing
            </Button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-auto rounded border" data-testid="deb-table">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking funded deals against Companies House…</div>
          ) : !data?.rows?.length ? (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="deb-empty">No deals with a debenture in this window.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Deal</th><th className="px-3 py-2">Funded</th><th className="px-3 py-2">Debenture from</th>
                  <th className="px-3 py-2">Legal Calendar</th><th className="px-3 py-2">Audit trail</th><th className="px-3 py-2">Companies House</th><th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.rows.map((r) => {
                  const st = STATUS[r.status] || { label: r.status, cls: "" };
                  const ch = r.companies_house || {};
                  return (
                    <tr key={r.sf_number} className="align-top" data-testid={`deb-row-${r.sf_number}`}>
                      <td className="px-3 py-2"><div className="font-medium">{r.company_name}</div><div className="font-mono text-[11px] text-muted-foreground">{r.sf_number}{r.company_number ? ` · ${r.company_number}` : ""}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap">{d(r.funded_at)}</td>
                      <td className="px-3 py-2" title={(r.evidence || []).join("\n")}>{SOURCE[r.debenture_source] || r.debenture_source || "—"}</td>
                      <td className="px-3 py-2">{r.event ? <>{r.event.status} · {d(r.event.date)}<div className="text-[11px] text-muted-foreground">deadline {d(r.event.mr01_deadline)}</div></> : <span className="text-red-700">no entry</span>}</td>
                      <td className="px-3 py-2">{r.audit_trail ? "✓" : <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2">
                        {!r.company_number ? <span className="text-muted-foreground">company number unknown</span>
                          : !ch.checked ? <span className="text-amber-700">{ch.error || "not checked"}</span>
                          : ch.registered ? <span className="text-emerald-700">charge {ch.charge?.charge_number || ""} created {d(ch.charge?.created_on)}</span>
                          : <span className="text-red-700">no charge since funding</span>}
                        {r.company_number && <a href={ch.url || `https://find-and-update.company-information.service.gov.uk/company/${r.company_number}/charges`} target="_blank" rel="noreferrer" className="ml-1 inline-flex align-middle text-sky-700" title="Open charges at Companies House"><ExternalLink className="w-3 h-3" /></a>}
                      </td>
                      <td className="px-3 py-2"><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${st.cls}`}>{r.status === "missing" || r.status === "marked_filed_not_at_ch" ? <AlertTriangle className="w-3 h-3" /> : null}{st.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {data && <p className="text-[11px] text-muted-foreground">Checked {new Date(data.generated_at).toLocaleTimeString("en-GB")} · "Registered" means a charge appears on the company's Companies House record created within 45 days before funding or later. Companies House results are cached for 12 hours.</p>}
      </DialogContent>
    </Dialog>
  );
}
